import React from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Separator } from './ui/separator';

const LAST_UPDATED = 'June 01, 2026';

export const PrivacyPolicy = ({ open, onClose }) => {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-black/50 backdrop-blur-sm overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="privacy-policy-modal"
    >
      <div className="w-full max-w-[720px] bg-white rounded-2xl shadow-2xl my-8 mx-4 sm:mx-6 relative">
        {/* Header */}
        <div className="sticky top-0 bg-white rounded-t-2xl border-b border-border/40 px-6 sm:px-8 py-5 flex items-center justify-between z-10">
          <h2 className="font-heading text-lg sm:text-xl font-bold text-foreground">Privacy Policy</h2>
          <button
            onClick={onClose}
            className="p-2 -mr-2 rounded-lg hover:bg-secondary/60 transition-colors"
            aria-label="Close privacy policy"
            data-testid="privacy-policy-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 sm:px-8 py-6 sm:py-8 space-y-6 text-[14px] text-muted-foreground leading-[1.7]">
          <p className="text-[12px] text-muted-foreground/60">
            Last Updated: {LAST_UPDATED}
          </p>

          <div>
            <h3 className="font-heading text-[15px] font-semibold text-foreground mb-2">Introduction</h3>
            <p>
              Apex Athletic Mind Consulting LLC (&ldquo;Company,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) is committed to protecting your privacy. This Privacy Policy describes how we collect, use, and disclose your personal information when you visit apexathleticmind.com, use the Apex AI Coach app, or engage in our mental performance coaching services.
            </p>
          </div>

          <Separator />

          <div>
            <h3 className="font-heading text-[15px] font-semibold text-foreground mb-2">Information We Collect</h3>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Direct Information:</strong> Name, email, phone number, athletic background, and payment information provided via Stripe.</li>
              <li><strong>Performance Data:</strong> Responses to the Apex Six assessments, self-reported mindset data, and progress tracking within the Apex AI Coach app.</li>
              <li><strong>Automated Data:</strong> IP addresses, browser types, device type, referring URL, and pages visited collected via cookies and analytics providers (such as Google Analytics).</li>
            </ul>
          </div>

          <Separator />

          <div>
            <h3 className="font-heading text-[15px] font-semibold text-foreground mb-2">Third-Party Processors</h3>
            <p className="mb-3">We utilize the following third-party service providers to facilitate our operations:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Payments:</strong> Stripe (PCI-compliant processing).</li>
              <li><strong>Communications:</strong> Resend (email delivery) and Zapier (workflow automation).</li>
              <li><strong>Application Hosting:</strong> Cloud-based servers for the Apex AI Coach app.</li>
              <li><strong>Analytics:</strong> Standard web and app analytics to improve user experience.</li>
            </ul>
          </div>

          <Separator />

          <div>
            <h3 className="font-heading text-[15px] font-semibold text-foreground mb-2">Data Rights (NJ, CCPA, GDPR)</h3>
            <p>
              We comply with applicable data protection laws. Users may request access to, correction of, or deletion of their personal data. We will respond to verified requests within thirty (30) days. While we primarily serve the NJ/NY/PA region, we apply these standard protections to all users.
            </p>
          </div>

          <Separator />

          <div>
            <h3 className="font-heading text-[15px] font-semibold text-foreground mb-2">California Privacy Rights (CCPA)</h3>
            <p>
              If you are a California resident, you have the right to request access to your data or its deletion. We do not sell your personal information.
            </p>
          </div>

          <Separator />

          <div>
            <h3 className="font-heading text-[15px] font-semibold text-foreground mb-2">Children&rsquo;s Privacy</h3>
            <p>
              Our services are often intended for high school and college athletes. For users under the age of 13, we require verifiable parental consent before collecting any personal information.
            </p>
          </div>

          <Separator />

          <div>
            <h3 className="font-heading text-[15px] font-semibold text-foreground mb-2">Contact Information</h3>
            <div className="mt-1 bg-[#F6F7F8] rounded-lg p-4">
              <p className="font-semibold text-foreground">Apex Athletic Mind Consulting LLC</p>
              <p>1820 Chapel Avenue W</p>
              <p>Suite 300-100A PMB 70263</p>
              <p>Cherry Hill, NJ 08002</p>
              <p>Email: <a href="mailto:volewis@apexathleticmind.com" className="text-[#004C54] font-medium hover:underline">volewis@apexathleticmind.com</a></p>
            </div>
          </div>

          {/* Close button at bottom */}
          <div className="pt-2">
            <Button
              onClick={onClose}
              className="bg-[#004C54] hover:bg-[#003a40] text-white font-semibold h-10 px-5 text-[13px] rounded-[10px]"
              data-testid="privacy-policy-close-button"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
